import React from 'react';

/**
 * Root error boundary.
 *
 * Before this, index.jsx mounted <App/> with no boundary, so any uncaught
 * render-time throw produced a white screen with no recovery path — which
 * is one of the ways an expired/corrupt session manifested (see cognito.js
 * getCurrentIdToken hardening). A boundary turns that into an actionable
 * card: clear the auth cache and reload to the Login screen.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('Uncaught render error:', error, info);
  }

  handleReset = () => {
    // Nuke every auth-related key (SDK + our mirror) and hard-reload so the
    // app remounts from a clean slate on the Login screen.
    try {
      const keys = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const k = localStorage.key(i);
        if (k && (k.startsWith('CognitoIdentityServiceProvider.') ||
                  k === 'auth_token' || k === 'admin_role' ||
                  k === 'admin_username' || k === 'admin_email')) {
          keys.push(k);
        }
      }
      keys.forEach((k) => localStorage.removeItem(k));
    } catch (_) { /* ignore */ }
    window.location.assign('/');
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--canvas, #F6F5FA)',
        padding: 'var(--sp-4, 16px)',
      }}>
        <div className="card" style={{ width: '100%', maxWidth: 420, textAlign: 'center' }} role="alert">
          <div className="empty-state-icon" style={{ margin: '0 auto var(--sp-4)' }} aria-hidden="true">!</div>
          <h3 style={{ margin: '0 0 var(--sp-2)' }}>Something went wrong</h3>
          <p className="text-muted" style={{ marginBottom: 'var(--sp-5)' }}>
            Your session may have expired. Sign in again to continue.
          </p>
          <button className="btn btn-primary btn-lg btn-block" onClick={this.handleReset}>
            Back to sign in
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
